import { PublishedFormId } from '@furnish-hx/constants/FormId';
import FormTypeId from '@core/constants/FormTypeId';
import { ClosetDoorShapeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import { MaterialId } from '@core/constants/Material';
import { ClosetDoorSensId } from '@core/constants/Option';
import fieldPortfolioData from './common/fieldsPortfolioData';
import {
    IFormFieldKey,
    IForm,
    IFormInputType,
    IDimensionUnit,
} from '../../interfaces/Form';
import additionalDataFactory from './common/fieldsAdditionalData';
import fieldsContact from './common/fieldsContact';
import {
    allGlasses,
    allMelamine,
    allMetals,
    allPlastics,
    allWoods,
    getMaterialsWithExceptions,
} from './common/materialGroups';

const closetDoorForm: IForm = {
    formId       : PublishedFormId.ClosetDoor,
    type         : FormTypeId.StorageUnit,
    thumbnailURL : '/resized-images/forms/closetDoor.jpg',
    fieldsOrder  : [
        IFormFieldKey.PortfolioData,
        IFormFieldKey.Comment,
        IFormFieldKey.ShapeDimension,
        IFormFieldKey.Type,
        IFormFieldKey.ClosetDoorSens,
        IFormFieldKey.Material,
        IFormFieldKey.ClosetDoorFrameMaterial,
        IFormFieldKey.AddOns,
        IFormFieldKey.AdditionalData,
        IFormFieldKey.Contact,
    ],
    fields: {
        portfolioData : fieldPortfolioData,
        comment       : {
            comment: {
                value    : undefined,
                required : false,
            },
        },
        shapeDimension: {
            shape: {
                value    : undefined,
                required : true,
                options  : [
                    ClosetDoorShapeId.ClosetNormal,
                    ClosetDoorShapeId.ClosetSlopeLeft,
                    ClosetDoorShapeId.ClosetSlopeRight,
                ],
            },
            dimension: {
                options: {
                    [ClosetDoorShapeId.ClosetNormal]: {
                        [FieldDimensionId.Width]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Height]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                    [ClosetDoorShapeId.ClosetSlopeLeft]: {
                        [FieldDimensionId.Height1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Diagonal2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 200, max: 2000 },
                            required     : true,
                            defaultValue : 500,
                        },
                        [FieldDimensionId.Width3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Height4]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width5]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                    [ClosetDoorShapeId.ClosetSlopeRight]: {
                        [FieldDimensionId.Height1]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width2]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Diagonal3]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 400, max: 3000 },
                            required     : true,
                            defaultValue : 1400,
                        },
                        [FieldDimensionId.Height4]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                        [FieldDimensionId.Width5]: {
                            value        : undefined,
                            type         : IFormInputType.Range,
                            unit         : IDimensionUnit.Centimeters,
                            step         : 10,
                            range        : { min: 500, max: 4000 },
                            required     : true,
                            defaultValue : 2000,
                        },
                    },
                },
            },
        },
        type: {
            value   : undefined,
            options : [
                ClosetDoorShapeId.ClosetFolding,
                ClosetDoorShapeId.ClosetSliding,
                ClosetDoorShapeId.ClosetSwing,
            ],
            required: true,
        },
        closetDoorSens: {
            value   : undefined,
            options : [
                ClosetDoorSensId.ClosetDoorRight,
                ClosetDoorSensId.ClosetDoorLeft,
            ],
            required: true,
        },
        material: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions(
                    [
                        ...allWoods,
                        ...allGlasses,
                        ...allMetals,
                        ...allPlastics,
                    ],
                    [
                        MaterialId.BlackLocust,
                        MaterialId.Iroko,
                        MaterialId.Douglas,
                        MaterialId.DouglasEngineered,
                        MaterialId.DouglasRecycled,
                        MaterialId.DouglasVeneer,
                        MaterialId.Larch,
                        MaterialId.LarchEngineered,
                        MaterialId.LarchRecycled,
                        MaterialId.LarchVeneer,
                        MaterialId.Padouk,
                        MaterialId.PadoukEngineered,
                        MaterialId.PadoukRecycled,
                        MaterialId.PadoukVeneer,
                        MaterialId.Ipe,
                        MaterialId.IpeRecycled,
                        MaterialId.IpeEngineered,
                        MaterialId.IpeVeneer,
                        MaterialId.Garapa,
                        MaterialId.PlexiglassFluorescent,
                        MaterialId.PlexiglassFrosted,
                        MaterialId.PlexiglassMirror,
                        MaterialId.PlexiglassTransparent,
                        MaterialId.PlexiglassWhite,
                        MaterialId.PolycarbonateOpaque,
                        MaterialId.PolycarbonateTeinted,
                        MaterialId.PolycarbonateTransparent,
                        MaterialId.RecycledPlastic,
                        MaterialId.SheetMetal,
                        MaterialId.WroughtIron,
                    ],
                ),
                required: true,
            },
        },
        closetDoorFrameMaterial: {
            material: {
                value   : undefined,
                options : getMaterialsWithExceptions([
                    ...allMelamine,
                    ...allWoods,
                    ...allMetals,
                    ...allPlastics,
                ],
                [
                    MaterialId.BlackLocust,
                    MaterialId.Iroko,
                    MaterialId.Douglas,
                    MaterialId.DouglasEngineered,
                    MaterialId.DouglasRecycled,
                    MaterialId.DouglasVeneer,
                    MaterialId.WagonfloorDark,
                    MaterialId.WagonfloorLight,
                    MaterialId.OakOld,
                    MaterialId.AshOlive,
                    MaterialId.AshOliveEngineered,
                    MaterialId.AshOliveRecycled,
                    MaterialId.AshOliveVeneer,
                    MaterialId.Acacia,
                    MaterialId.AcaciaEngineered,
                    MaterialId.AcaciaRecycled,
                    MaterialId.AcaciaVeneer,
                    MaterialId.Mahogany,
                    MaterialId.MahoganyEngineered,
                    MaterialId.MahoganyRecycled,
                    MaterialId.MahoganyVeneer,
                    MaterialId.Alderwood,
                    MaterialId.AlderwoodEngineered,
                    MaterialId.AlderwoodRecycled,
                    MaterialId.AlderwoodVeneer,
                    MaterialId.Maple,
                    MaterialId.MapleEngineered,
                    MaterialId.MapleRecycled,
                    MaterialId.MapleVeneer,
                    MaterialId.Teak,
                    MaterialId.TeakEngineered,
                    MaterialId.TeakRecycled,
                    MaterialId.TeakVeneer,
                    MaterialId.Wenge,
                    MaterialId.WengeEngineered,
                    MaterialId.WengeRecycled,
                    MaterialId.WengeVeneer,
                    MaterialId.Cherrywood,
                    MaterialId.CherrywoodEngineered,
                    MaterialId.CherrywoodRecycled,
                    MaterialId.CherrywoodVeneer,
                    MaterialId.Ebony,
                    MaterialId.EbonyEngineered,
                    MaterialId.EbonyRecycled,
                    MaterialId.EbonyVeneer,
                    MaterialId.Garapa,
                    MaterialId.PlexiglassFluorescent,
                    MaterialId.PlexiglassFrosted,
                    MaterialId.PlexiglassMirror,
                    MaterialId.PlexiglassTransparent,
                    MaterialId.PlexiglassWhite,
                    MaterialId.PolycarbonateOpaque,
                    MaterialId.PolycarbonateTeinted,
                    MaterialId.PolycarbonateTransparent,
                    MaterialId.RecycledPlastic,
                    MaterialId.Steel,
                    MaterialId.Rust,
                    MaterialId.Brass,
                    MaterialId.Iron,
                    MaterialId.ThermoLaqueredBlack,
                    MaterialId.ThermoLaqueredWhite,
                    MaterialId.SheetMetal,
                    MaterialId.StainlessSteel,
                    MaterialId.Zinc,
                    MaterialId.WroughtIron,
                ]),
                required: false,
            },
        },
        addOns: {
            [ClosetDoorShapeId.ClosetHandle]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
            [ClosetDoorShapeId.ClosetLock]: {
                value : undefined,
                min   : 0,
                max   : 1,
            },
        },
        additionalData: additionalDataFactory({
            budgetMin          : 700,
            budgetMax          : 3200,
            budgetDefaultValue : 1300,
        }),
        contact: fieldsContact,
    },
};

export default closetDoorForm;
